
import {Utils} from '../common/Utils';
import {Response} from "./Response";

/**
 * @typedef {XMLHttpRequest} jqXHR - jQuery version of XMLHttpRequest
 */

/**
 * Base API entry point from ARGO
 * @type {string}
 */
const BASE_API = '/api';
/**
 * API path for a single entry
 * @type {string}
 */
const ENTRY_URI = '/{collection}/{id}';
/**
 * API path for a query (entry list)
 * @type {string}
 */
const QUERY_URI = '/{collection}/query';

/**
 * This class provides functionality to make a ARGO API request by using jQuery's ajax function.
 * All CRUD methods are implemented for a single data entry and the ARGO specific query method that
 * returns a list of data entries.
 *
 * By working with the ARGO collection "dataEntries" it's recommended to use {@link Model} for a single
 * data entry or {@link Collection} for a query request (multiple data entries)
 */
export class Request {

  /**
   * Configuration object for the {@link Request} class.
   * @typedef {Object} RequestConfig
   * @property {String} [collection=dataEntries] - name of the ARGO collection to be requested
   * @property {String} [contentType=application/json] - content type to be set in the Ajax request
   * @property {String} [url=null] - API URI to the API, by default ENTRY_URI or QUERY_URI
   * @property {Object<String,String>} [xhr] - key value map additional XHR settings to be set
   */

  /**
   * Initializes a request object
   * @param {RequestConfig} config
   */
  constructor(config = {}) {
    /**
     * name of the ARGO collection to be requested
     * @type {string}
     */
    this.collection = config.collection || 'dataEntries';
    /**
     * content type to be set in the Ajax request
     * @type {string}
     */
    this.contentType = !Utils.isUndefOrNull(config.contentType) ? config.contentType : 'application/json';
    /**
     * A map of all headers to be sent with the Ajax request.
     * By default "X-Requested-With" is set to "XMLHttpRequest" as required by ARGO.
     * @type {Options<String,String>}
     */
    this.headers = {
      'X-Requested-With': 'XMLHttpRequest'
    };
    /**
     * API path to be used
     * @type {String|null}
     */
    this.url = config.url || null;
    /**
     * additional xhr configuration
     * @type {Object<String, String>|{}}
     */
    this.xhrConfig = config.xhr || {};
    /**
     * References a manually set "beforeHandler"
     * @type {beforeSend}
     * @private
     */
    this._beforeHandler = null;
    /**
     * Reference to the response instance that contains origin xhr header
     * @type {Response}
     * @private
     */
    this.response = null;
    /**
     * Textual message set by jQuery in success / error handler
     * @type {String|null}
     * @private
     */
    this._textStatus = null;
    /**
     * Store original config
     * @type {RequestConfig}
     */
    this.config = config;
  }

  /**
   * This callback is displayed as a global member.
   * @callback beforeSend
   * @param {jqXHR} jqXHR
   * @param {Object<String,String>} settings
   */
  /**
   * A pre-request callback function that can be used to modify the XHR object before it is sent.
   * @param {beforeSend} fn - callback function
   * @return {Request}
   */
  before(fn) {
    if (Utils.isFunction(fn)) {
      this._beforeHandler = fn;
    }
    return this;
  }

  /**
   * Add a request header with given key and value.
   * @param {String} key - name of the header field
   * @param {String} value - header field's value
   * @return {Request}
   */
  addHeader(key, value) {
    if (Utils.isString(key)) {
      this.headers[key] = value;
      return this;
    }
    // object notation (multiple headers)
    for (let i in key) {
      this.headers[i] = key[i];
    }
    return this;
  }

  /**
   * Replaces placeholder in the given path with parameter defined in params.
   * Empty or not defined placeholder will be removed from the path.
   * @param {String} path -
   * @param {Object<String,String|Number>} params - parameter to be injected into the path
   * @return {string}
   */
  transformUri(path, params = {}) {
    return BASE_API + Utils.insertPlaceholder(path, params)
      .replace(/\{[A-Za-z]+\}/g, "") // remove empty placeholder
      .replace(/[\/]+$/, "");        // remove dangling slashes
  }

  /**
   * Load data from the ARGO API endpoint using a HTTP GET request.
   * @param {String|Number} id - entry id from the dataset to be fetched
   * @return {Promise}
   */
  get(id) {
    return this.ajax({
      type: 'GET',
      url: this.transformUri(this.url || ENTRY_URI, {
        collection: this.collection,
        id: id
      })
    });
  }

  /**
   * Load data from the API using a HTTP POST request.
   * Send the given data as request body to the backend endpoint. Commonly used to create a new dataset
   * on the server. Once a new data entry was created on the server, the new dataset gets a new unique ID for
   * identification. The URI to the new dataset must be extracted from the "Location" field in the Response-Header.
   *
   * Important: The given id represents a special sub-path, e.g. PW reset id or accept terms id, but this is NOT
   * a data entry ID. For modifying a dataset which already exists in ARGO, use {@link patch} or {@link put}.
   * @param {String|Number} [id] - can be a special subpath, e.g. PW reset id or accept terms id; this is NOT a data entry
   * @param {Object<String,String|Number|Boolean|null>} data - plain object that will be serialized
   * @return {Promise}
   */
  post(id, data) {

    let urlParam = {
      collection: this.collection
    };
    if ($.isPlainObject(id)) {
      data = id;
      id = null;
    }
    if (!Utils.isUndefOrNull(id)) {
      urlParam.id = id;
    }

    return this.ajax({
      type: 'POST',
      data,
      url: this.transformUri(this.url || ENTRY_URI, urlParam)
    });
  }

  /**
   * Load data from the API using a HTTP PUT request.
   * Send the given data as request body to the backend endpoint. Used to update a dataset on the server.
   * All properties from the dataset must be sent to the server. To update a single property from a dataset,
   * use {@link patch}.
   * @param {String|Number} id - unique ID from the dataset
   * @param {Object<String,String|Number|Boolean|null>} data - whole data set
   * @return {Promise}
   */
  put(id, data) {
    return this.ajax({
      type: 'PUT',
      data,
      url: this.transformUri(this.url || ENTRY_URI, {
        collection: this.collection,
        id
      })
    });
  }

  /**
   * Load data from the API using a HTTP PATCH request.
   * Send the given data as request body to the backend endpoint. Used to update a dataset on the server.
   * Only changed properties from the dataset must be sent to the server.
   * @param {String|Number} id - unique ID from the dataset
   * @param {Object<String,String|Number|Boolean|null>} data - properties from the dataset to be changed
   * @param {Object|null} callback
   * @return {Promise}
   */
  patch(id, data, callback = null) {
    return this.ajax({
      type: 'PATCH',
      data,
      callback,
      url: this.transformUri(this.url || ENTRY_URI, {
        collection: this.collection,
        id
      })
    });
  }

  /**
   * Load data from the API using a HTTP DELETE request.
   * Removes the dataset with the given id from the ARGO server.
   * @param {String|Number} id - unique ID from the dataset
   * @return {Promise}
   */
  delete(id) {
    return this.ajax({
      type: 'DELETE',
      url: this.transformUri(this.url || ENTRY_URI, {
        collection: this.collection,
        id
      })
    });
  }

  /**
   * Configuration object to filter a query in {@link QueryOptions}.
   * @typedef {Object} QueryFilter
   * @property {String} property - property name
   * @property {String} [operator=EQ] - operator
   * @property {String|Number|Boolean|null} value - value to be matched. can also be an array.
   */
  /**
   * Configuration object for query function in the {@link Request} class.
   * @typedef {Object} QueryOptions
   * @property {String} [type] - ARGO content type (mandatory if collection is dataEntries)
   * @property {Array<QueryFilter>} [filters] - filters the result set
   * @property {String|Array<String>} [expand] - comma separated list of foreign keys to be expanded or array of foreign keys (only available for dataEntries)
   * @property {String} [sort] - sorting datasets by properties, format: <propertyName>,ASC|DESC;...
   * @property {Number} [page=0] - page number
   * @property {Number} [pageSize=25] - number of datasets per page
   */
  /**
   * Load data from the API using a HTTP GET request.
   * Returns multiple datasets according to the given query options.
   * @param {QueryOptions} options - query options to be passed as GET parameters to the ARGO API
   * @return {Promise}
   */
  query(options) {

    //let data = {...options};
    let data = Object.assign({}, options);

    if (options.filters) {
      data.filters = JSON.stringify(options.filters);
    }

    if (options.expand && Array.isArray(options.expand)) {
      data.expand = options.expand.join(",");
    }

    if (options.sort) {
      // converts multiple sorting properties into array
      data.sort = options.sort.split(";");
    }

    return this.ajax({
      traditional: true, // ensures spring boot compatibility
      type: 'GET',
      data,
      url: this.transformUri(this.url || QUERY_URI, {
        collection: this.collection
      })
    });
  }

  /**
   * Ajax call to be sent to the ARGO API
   * @param {Object} options
   * @return {Response}
   */
  ajax(options) {

    let params = $.extend({}, this.xhrConfig, {
      beforeSend: this._beforeHandler
    });

    if (!Utils.isUndefOrNull(this.contentType)) {
      params.contentType = this.contentType;
    }
    if (Utils.isString(params.contentType) && params.contentType.indexOf('json') !== -1 && options.data && options.type !== 'GET') {
      options.data = JSON.stringify(options.data);
    }

    params = $.extend(true, params, {
      headers: this.headers,
      xhrFields: { withCredentials: true },
    }, options);

    return this.response = new Response(this, $.ajax(params), options.callback);
  }

}